import React from 'react';
import {view, Spinner} from '@sail/ui';
import type {CssProperties} from '@sail/ui';
import {Text, TextType} from 'src/v2/components/common/Text';
import {COLORS} from 'src/v2/util/color';
import {getAccessibleTextColor} from 'src/v2/util/getAccessibleTextColor';

// Common shadow values for button states
const BASE_SHADOW =
  'inset 0 0 0 1px rgba(50, 50, 93, 0.1), 0 6px 15px 0 rgba(50, 50, 93, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.1)';
const FOCUS_RING = '0 0 0 4px rgba(50, 151, 211, 0.3)';

type ButtonProps = {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  css?: CssProperties;
  textCss?: CssProperties;
  type?: 'primary' | 'secondary';
  textType?: TextType;
};

export const Button = ({
  children,
  onClick,
  disabled,
  loading,
  type = 'primary',
  css,
  textCss,
  textType = 'bodyEmphasized',
}: ButtonProps) => {
  // Get the actual background color (default or from css override)
  const backgroundColor =
    type === 'primary'
      ? (css?.backgroundColor as string) || COLORS.defaultButtonColor
      : 'transparent';

  let buttonTextColor;

  if (type === 'primary') {
    if (disabled) {
      buttonTextColor = getAccessibleTextColor(backgroundColor, 'disabled');
    } else {
      buttonTextColor = getAccessibleTextColor(backgroundColor, 'primary');
    }
  } else {
    // Secondary buttons have transparent background, so use light background text color
    buttonTextColor = COLORS.textSubduedOnLight;
  }

  return (
    <view.button
      onClick={onClick}
      disabled={disabled || loading}
      css={{
        width: 'fill',
        backgroundColor,
        borderRadius: 'small',
        border: 'none',
        padding: 'space.150',
        transition: 'all 250ms ease-in-out',
        fontFamily: 'inherit',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        color: buttonTextColor,
        ':hover': !(disabled || loading)
          ? {
              cursor: 'pointer',
              opacity: 0.9,
              color: type === 'primary' ? buttonTextColor : COLORS.textPrimary,
              backgroundColor:
                type === 'primary'
                  ? backgroundColor
                  : COLORS.offsetHoverOnLight,
            }
          : {},
        ':focus': !(disabled || loading)
          ? {
              outline: 'none',
              boxShadow: `${BASE_SHADOW}, ${FOCUS_RING}`,
            }
          : {},
        ...css,
      }}
    >
      <Text
        type={textType}
        css={{
          color: 'inherit',
          ...textCss,
        }}
      >
        {children}
      </Text>
      {loading ? (
        <Spinner
          css={{
            width: 'medium',
            height: 'medium',
            color: buttonTextColor,
            position: 'absolute',
            right: 'space.500',
          }}
        />
      ) : null}
    </view.button>
  );
};
